import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import VAHeaderNav from '../../components/navigation/VAHeaderNav';
import Footer from '../../components/layout/Footer';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

// Skill category data
const skillCategories = [
  {
    id: 'administrative',
    name: 'Administrative',
    icon: 'FileText',
    color: 'blue',
    description: 'Essential administrative skills for virtual assistants',
    skills: [
      'Email Management', 'Calendar Management', 'Data Entry', 'Document Preparation', 
      'Meeting Coordination', 'Travel Arrangements', 'File Organization', 'Transcription',
      'Customer Support', 'Appointment Scheduling', 'Office Administration', 'Phone Handling'
    ]
  },
  {
    id: 'technical',
    name: 'Technical',
    icon: 'Code',
    color: 'green',
    description: 'Technical skills for specialized virtual assistance',
    skills: [
      'WordPress Management', 'HTML/CSS', 'Basic Programming', 'CRM Management', 
      'Website Maintenance', 'E-commerce Management', 'Technical Support', 'Database Management',
      'SEO Implementation', 'API Integration', 'Software Testing', 'IT Support'
    ]
  },
  {
    id: 'creative',
    name: 'Creative',
    icon: 'Palette',
    color: 'purple',
    description: 'Creative skills for content creation and design',
    skills: [
      'Graphic Design', 'Content Creation', 'Video Editing', 'Photo Editing', 
      'Illustration', 'Animation', 'Presentation Design', 'Infographic Creation',
      'Audio Editing', 'Brand Identity Design', 'UI/UX Design', 'Creative Writing'
    ]
  },
  {
    id: 'communication',
    name: 'Communication',
    icon: 'MessageCircle',
    color: 'orange',
    description: 'Communication skills for effective client interaction',
    skills: [
      'Business Writing', 'Email Communication', 'Client Relations', 'Phone Communication', 
      'Chat Support', 'Public Relations', 'Multilingual Support', 'Proofreading',
      'Editing', 'Copywriting', 'Report Writing', 'Internal Communications'
    ]
  },
  {
    id: 'marketing',
    name: 'Marketing',
    icon: 'TrendingUp',
    color: 'pink',
    description: 'Marketing skills for business growth and promotion',
    skills: [
      'Social Media Management', 'Email Marketing', 'Content Marketing', 'Digital Marketing', 
      'SEO', 'SEM', 'Market Research', 'Campaign Management',
      'Influencer Outreach', 'Analytics Reporting', 'Brand Management', 'Marketing Strategy'
    ]
  },
  {
    id: 'project-management',
    name: 'Project Management',
    icon: 'Calendar',
    color: 'indigo',
    description: 'Project management skills for efficient workflow',
    skills: [
      'Project Coordination', 'Task Management', 'Team Coordination', 'Workflow Optimization', 
      'Process Documentation', 'Resource Allocation', 'Timeline Management', 'Risk Assessment',
      'Project Planning', 'Status Reporting', 'Agile Methodologies', 'Project Tracking'
    ]
  },
  {
    id: 'data-analysis',
    name: 'Data Analysis',
    icon: 'BarChart',
    color: 'cyan',
    description: 'Data analysis skills for informed decision making',
    skills: [
      'Data Entry & Cleaning', 'Spreadsheet Management', 'Basic Data Analysis', 'Report Generation', 
      'Data Visualization', 'Survey Analysis', 'KPI Tracking', 'Performance Metrics',
      'Statistical Analysis', 'Dashboard Creation', 'Business Intelligence', 'Data Interpretation'
    ]
  },
  {
    id: 'finance',
    name: 'Finance',
    icon: 'DollarSign',
    color: 'emerald',
    description: 'Financial skills for business accounting and bookkeeping',
    skills: [
      'Bookkeeping', 'Invoice Management', 'Expense Tracking', 'Financial Reporting', 
      'Budget Management', 'Payroll Processing', 'Account Reconciliation', 'Financial Data Entry',
      'QuickBooks', 'Xero', 'FreshBooks', 'Financial Documentation'
    ]
  }
];

// Skill Card Component
const SkillCard = ({ skill, category }) => {
  const getColorClass = (color) => {
    const colorMap = {
      blue: 'bg-blue-50 text-blue-700 border-blue-200',
      green: 'bg-green-50 text-green-700 border-green-200',
      purple: 'bg-purple-50 text-purple-700 border-purple-200',
      orange: 'bg-orange-50 text-orange-700 border-orange-200',
      pink: 'bg-pink-50 text-pink-700 border-pink-200',
      indigo: 'bg-indigo-50 text-indigo-700 border-indigo-200',
      cyan: 'bg-cyan-50 text-cyan-700 border-cyan-200',
      emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200'
    };
    return colorMap[color] || 'bg-gray-50 text-gray-700 border-gray-200';
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-all">
      <div className="flex items-center justify-between mb-2">
        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getColorClass(category.color)}`}>
          {category.name}
        </span>
        <Icon name={category.icon} size={16} className={`text-${category.color}-600`} />
      </div>
      <h3 className="font-medium text-gray-900">{skill}</h3>
    </div>
  );
};

// Category Section Component
const CategorySection = ({ category, isExpanded, onToggle, searchTerm }) => {
  // Filter skills based on search term
  const filteredSkills = searchTerm 
    ? category.skills.filter(skill => 
        skill.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : category.skills;

  if (searchTerm && filteredSkills.length === 0) {
    return null;
  }

  const getColorClass = (color) => {
    const colorMap = {
      blue: 'bg-blue-50 text-blue-700',
      green: 'bg-green-50 text-green-700',
      purple: 'bg-purple-50 text-purple-700',
      orange: 'bg-orange-50 text-orange-700',
      pink: 'bg-pink-50 text-pink-700',
      indigo: 'bg-indigo-50 text-indigo-700',
      cyan: 'bg-cyan-50 text-cyan-700',
      emerald: 'bg-emerald-50 text-emerald-700'
    };
    return colorMap[color] || 'bg-gray-50 text-gray-700';
  };

  return (
    <div className="mb-10">
      <div 
        className="flex items-center justify-between cursor-pointer mb-4"
        onClick={onToggle}
      >
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg ${getColorClass(category.color)} flex items-center justify-center`}>
            <Icon name={category.icon} size={20} />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900">{category.name}</h2>
            <p className="text-sm text-gray-600">{filteredSkills.length} skills</p>
          </div>
        </div>
        <button className="p-1 hover:bg-gray-100 rounded-full transition-colors">
          <Icon name={isExpanded ? "ChevronUp" : "ChevronDown"} size={20} className="text-gray-500" />
        </button>
      </div>

      {isExpanded && (
        <>
          <p className="text-gray-600 mb-6">{category.description}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {filteredSkills.map((skill) => (
              <SkillCard key={skill} skill={skill} category={category} />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// Main Component
const SkillsDirectoryPage = () => {
  const [expandedCategories, setExpandedCategories] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');

  // Initialize all categories as expanded
  useEffect(() => {
    const initialExpanded = {};
    skillCategories.forEach(category => {
      initialExpanded[category.id] = true;
    });
    setExpandedCategories(initialExpanded);
  }, []);

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    // Expand all categories when searching
    if (e.target.value) {
      const allExpanded = {};
      skillCategories.forEach(category => {
        allExpanded[category.id] = true;
      });
      setExpandedCategories(allExpanded);
    }
  };

  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
  };

  // Filter categories based on active filter
  const filteredCategories = activeFilter === 'all' 
    ? skillCategories 
    : skillCategories.filter(category => category.id === activeFilter);

  return (
    <>
      <Helmet>
        <title>Skills Directory | VA Sprouts</title>
        <meta name="description" content="Browse our comprehensive directory of virtual assistant skills across multiple categories." />
      </Helmet>

      <VAHeaderNav />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-50 to-blue-50 pt-20 pb-16 md:pt-28 md:pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Skills Directory</h1>
            <p className="text-xl text-gray-700 mb-8 max-w-3xl mx-auto">
              Browse our comprehensive directory of virtual assistant skills across multiple categories
            </p>
          </div>
        </div>
      </section>

      {/* Search and Filters */}
      <section className="bg-white py-8 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row gap-4 md:items-center justify-between">
            <div className="relative md:w-1/3">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Icon name="Search" size={20} className="text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search skills..."
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                value={searchTerm}
                onChange={handleSearch}
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeFilter === 'all'
                    ? 'bg-primary text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                onClick={() => handleFilterChange('all')}
              >
                All Categories
              </button>
              {skillCategories.map((category) => (
                <button
                  key={category.id}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeFilter === category.id
                      ? 'bg-primary text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  onClick={() => handleFilterChange(category.id)}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Skills Directory */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredCategories.map((category) => (
            <CategorySection
              key={category.id}
              category={category}
              isExpanded={expandedCategories[category.id]}
              onToggle={() => toggleCategory(category.id)}
              searchTerm={searchTerm}
            />
          ))}

          {/* No Results */}
          {filteredCategories.length === 0 && (
            <div className="text-center py-16">
              <Icon name="Search" size={48} className="text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No skills found</h3>
              <p className="text-gray-500 mb-4">
                Try adjusting your search or filter settings
              </p>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => {
                  setSearchTerm('');
                  setActiveFilter('all');
                }}
              >
                Clear Filters
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-primary rounded-xl p-8 md:p-12 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">Ready to Showcase Your Skills?</h2>
            <p className="text-lg text-white opacity-90 max-w-3xl mx-auto mb-8">
              Create your profile and get verified in the skills you excel at. Connect with employers looking for your expertise.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/login-register?tab=register&role=va">
                <Button 
                  variant="white" 
                  size="lg" 
                  className="px-8"
                >
                  Join as a VA
                </Button>
              </Link>
              <Link to="/login-register?tab=register&role=employer">
                <Button 
                  variant="outline-white" 
                  size="lg" 
                  className="px-8"
                >
                  Hire a VA
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
      
      {/* Footer */}
      <Footer />
    </>
  );
};

export default SkillsDirectoryPage;
